import api, { handleResponse, handleError } from './api';

/**
 * External event ingestion control plane. Superadmin-only — every endpoint sits
 * behind /api/admin/ingestion.
 *
 * Note there is deliberately no public counterpart: sources are internal, and an
 * ingested event is indistinguishable from a host-authored one to guests.
 */
const ingestionService = {
  async getSources() {
    try {
      return handleResponse(await api.get('/admin/ingestion/sources'));
    } catch (error) {
      return handleError(error);
    }
  },

  async createSource(payload) {
    try {
      return handleResponse(await api.post('/admin/ingestion/sources', payload));
    } catch (error) {
      return handleError(error);
    }
  },

  async updateSource(id, payload) {
    try {
      return handleResponse(await api.patch(`/admin/ingestion/sources/${id}`, payload));
    } catch (error) {
      return handleError(error);
    }
  },

  /** Parse and report what WOULD change. Writes nothing — use before enabling a source. */
  async dryRun(id) {
    try {
      return handleResponse(await api.post(`/admin/ingestion/sources/${id}/dry-run`));
    } catch (error) {
      return handleError(error);
    }
  },

  async runNow(id, { publish = false } = {}) {
    try {
      return handleResponse(await api.post(`/admin/ingestion/sources/${id}/run`, null, { params: { publish } }));
    } catch (error) {
      return handleError(error);
    }
  },

  async uploadCsv(id, csv) {
    try {
      return handleResponse(await api.post(`/admin/ingestion/sources/${id}/csv`, { csv }));
    } catch (error) {
      return handleError(error);
    }
  },

  async getQueue(status = 'new', limit = 50) {
    try {
      return handleResponse(await api.get(`/admin/ingestion/queue?status=${status}&limit=${limit}`));
    } catch (error) {
      return handleError(error);
    }
  },

  async review(itemId, action, notes) {
    try {
      return handleResponse(await api.post(`/admin/ingestion/queue/${itemId}/review`, { action, notes }));
    } catch (error) {
      return handleError(error);
    }
  },

  async approveAll() {
    try {
      return handleResponse(await api.post('/admin/ingestion/queue/approve-all'));
    } catch (error) {
      return handleError(error);
    }
  },

  async purge() {
    try {
      return handleResponse(await api.post('/admin/ingestion/purge'));
    } catch (error) {
      return handleError(error);
    }
  },

  async getPendingClaims() {
    try {
      return handleResponse(await api.get('/admin/ingestion/claims'));
    } catch (error) {
      return handleError(error);
    }
  },

  async resolveClaim(eventId, action) {
    try {
      return handleResponse(await api.post(`/admin/ingestion/claims/${eventId}/${action}`));
    } catch (error) {
      return handleError(error);
    }
  },

  async getHealth() {
    try {
      return handleResponse(await api.get('/admin/ingestion/health'));
    } catch (error) {
      return handleError(error);
    }
  },

  async seedSources() {
    try {
      return handleResponse(await api.post('/admin/ingestion/seed-sources'));
    } catch (error) {
      return handleError(error);
    }
  },
};

export default ingestionService;
